<script setup lang="ts">
import Header from '@/components/Common/Header.vue'
import MobileHeader from '@/components/Common/MobileHeader.vue'
import { watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import i18n from './i18n'
import { useLangStore } from '@/pinia/stores/lang'

const langStore = useLangStore()
const route = useRoute()

const localeByPathSegment = {
  zh: 'zh-CN',
  en: 'en-US'
} as const

const pathSegmentByLocale = {
  'zh-CN': 'zh',
  'en-US': 'en'
} as const

const storageKey = 'site_lang'
const siteOrigin = (import.meta.env.VITE_SITE_ORIGIN as string | undefined)?.replace(/\/$/, '')
const localePathPattern = /^\/(zh|en)(?=\/|$)/i

const toSupportedLocale = (lang: string | null | undefined) =>
  typeof lang === 'string' && lang.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US'

const normalizePath = (path: string) => {
  const trimmed = path.replace(/\/{2,}/g, '/')
  if (trimmed === '') return '/'
  if (trimmed.length > 1 && trimmed.endsWith('/')) {
    return trimmed.slice(0, -1)
  }
  return trimmed
}

const getLocaleFromPathname = (pathname: string) => {
  const matched = pathname.match(localePathPattern)
  if (!matched) return null
  const segment = matched[1].toLowerCase() as keyof typeof localeByPathSegment
  return localeByPathSegment[segment]
}

const getBasePathname = (pathname: string) => {
  const normalized = normalizePath(pathname)
  const stripped = normalized.replace(localePathPattern, '') || '/'
  return normalizePath(stripped)
}

const buildLocalizedPathname = (basePathname: string, locale: 'zh-CN' | 'en-US') => {
  const segment = pathSegmentByLocale[locale]
  const normalizedBase = normalizePath(basePathname)
  return normalizedBase === '/' ? `/${segment}` : `/${segment}${normalizedBase}`
}

const getInitialLocale = () => {
  const localeFromPath = getLocaleFromPathname(window.location.pathname)
  if (localeFromPath) {
    return localeFromPath
  }

  const savedLang = window.localStorage.getItem(storageKey)
  if (savedLang) {
    return toSupportedLocale(savedLang)
  }

  return 'zh-CN'
}

const upsertHeadLink = (selector: string, rel: string, href: string, hreflang?: string) => {
  let link = document.head.querySelector(selector) as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.rel = rel
    document.head.appendChild(link)
  }
  link.href = href
  if (hreflang) {
    link.hreflang = hreflang
  } else {
    link.removeAttribute('hreflang')
  }
}

const syncLanguageMeta = (locale: 'zh-CN' | 'en-US') => {
  const currentUrl = new URL(window.location.href)
  currentUrl.hash = ''
  currentUrl.search = ''
  const basePathname = getBasePathname(currentUrl.pathname)
  const zhUrl = new URL(currentUrl.toString())
  zhUrl.pathname = buildLocalizedPathname(basePathname, 'zh-CN')
  const enUrl = new URL(currentUrl.toString())
  enUrl.pathname = buildLocalizedPathname(basePathname, 'en-US')
  const canonicalUrl = locale === 'zh-CN' ? zhUrl : enUrl
  const toHref = (url: URL) => {
    const pathWithQuery = `${url.pathname}${url.search}`
    return siteOrigin ? `${siteOrigin}${pathWithQuery}` : pathWithQuery
  }

  upsertHeadLink('link[rel="canonical"]', 'canonical', toHref(canonicalUrl))
  upsertHeadLink('link[rel="alternate"][hreflang="zh-cn"]', 'alternate', toHref(zhUrl), 'zh-cn')
  upsertHeadLink('link[rel="alternate"][hreflang="en-us"]', 'alternate', toHref(enUrl), 'en-us')
  upsertHeadLink('link[rel="alternate"][hreflang="x-default"]', 'alternate', toHref(enUrl), 'x-default')
}

const syncLanguage = (lang: string | null | undefined) => {
  const locale = toSupportedLocale(lang)
  if (langStore.currentLang !== locale) {
    langStore.setLang(locale)
  }
  if (i18n.global.locale.value !== locale) {
    i18n.global.locale.value = locale
  }

  const nextUrl = new URL(window.location.href)
  const basePathname = getBasePathname(nextUrl.pathname)
  const localizedPathname = buildLocalizedPathname(basePathname, locale)
  if (nextUrl.pathname !== localizedPathname) {
    nextUrl.pathname = localizedPathname
    window.history.replaceState({}, '', nextUrl.toString())
  }

  document.documentElement.lang = locale
  window.localStorage.setItem(storageKey, locale)
  syncLanguageMeta(locale)
}

onMounted(() => {
  syncLanguage(getInitialLocale())
})

watch(
  () => route.fullPath,
  () => {
    const localeFromPath = getLocaleFromPathname(window.location.pathname)
    syncLanguage(localeFromPath ?? langStore.currentLang)
  }
)

watch(
  () => langStore.currentLang,
  (newLang: string) => {
    syncLanguage(newLang)
  },
  { immediate: true }
)

watch(
  () => i18n.global.locale.value,
  (newLang) => {
    syncLanguage(newLang)
  }
)
</script>

<template>
  <div class="app">
    <!-- 桌面端Header -->
    <Header class="desktop-only" />
    <!-- 移动端Header -->
    <MobileHeader class="mobile-only" />
    <router-view></router-view>
  </div>
</template>

<style lang="scss">
.app {
  min-height: 100vh;
  background: url('/images/背景1.jpg') no-repeat center center fixed;
  background-size: cover;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.7) 0%, rgba(189, 215, 238, 0.6) 100%);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 0;
    backdrop-filter: blur(12px) saturate(180%);
  }

  > * {
    position: relative;
    z-index: 2;
  }
  
} 

// 显示控制类
.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }
}

// 重置默认样式
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  line-height: 1.5;
}

a {
  text-decoration: none;
  color: inherit;
}

img {
  max-width: 100%;
  height: auto;
}
</style>
